import { apiRequest, ApiError, getApiBaseUrl } from "@/lib/api/client";
import { getAuthToken } from "@/lib/api/token-store";

function createAuthHeaders(token, headers = {}) {
  const normalizedToken = String(token || getAuthToken() || "").trim();
  if (!normalizedToken) {
    throw new ApiError("authorization token is required", 401);
  }
  return {
    Authorization: `Bearer ${normalizedToken}`,
    ...headers,
  };
}

/** @typedef {{ userId: string, source: 'direct' | 'team' | 'both' }} ThreadMemberUserEntry */

/**
 * GET /api/admin/threads/{threadID}/members — same payload as non-admin route; requires active employee JWT.
 * @returns {{ threadId: string, users: ThreadMemberUserEntry[], userIds: string[], botIds: string[], raw: * }}
 */
export async function fetchAdminThreadMembers({ token, threadId, suppressPermissionDeniedEvent = false } = {}) {
  const normalizedThreadId = String(threadId || "").trim();
  if (!normalizedThreadId) {
    throw new ApiError("threadId is required", 400);
  }

  const payload = await apiRequest(
    `/api/admin/threads/${encodeURIComponent(normalizedThreadId)}/members`,
    {
      method: "GET",
      headers: createAuthHeaders(token),
      suppressPermissionDeniedEvent,
    },
  );

  const rawUsers = Array.isArray(payload?.users) ? payload.users : [];
  /** @type {ThreadMemberUserEntry[]} */
  const users = rawUsers
    .map((u) => {
      const userId = String(u?.userId || "").trim();
      if (!userId) return null;
      const s = String(u?.source || "direct").toLowerCase();
      const source = s === "team" || s === "both" ? s : "direct";
      return { userId, source };
    })
    .filter(Boolean);

  const userIds = Array.isArray(payload?.userIds) ? payload.userIds.map((id) => String(id || "").trim()).filter(Boolean) : [];
  const botIds = Array.isArray(payload?.botIds) ? payload.botIds.map((id) => String(id || "").trim()).filter(Boolean) : [];

  return {
    threadId: String(payload?.threadId || normalizedThreadId).trim(),
    users,
    userIds,
    botIds,
    raw: payload,
  };
}

/**
 * GET /api/admin/users/{userID} — full user document; requires active employee JWT.
 * @returns {Promise<Record<string, *>>}
 */
/**
 * Resolve many admin user records in ONE request, keyed by the ids you passed in.
 *
 * The per-user GET is a distinct url per id, so the thread member panel issued one per member AND
 * one CORS preflight per member (the preflight cache is per-url), which then queued against the
 * browser's connection limit. POST to a constant url instead: one request, one cacheable preflight.
 *
 * Ids that do not exist — or that the caller is not permitted to read — are absent from the map.
 */
export async function fetchAdminUsers({ token, userIds } = {}) {
  const ids = Array.isArray(userIds)
    ? [...new Set(userIds.map((id) => String(id || "").trim()).filter(Boolean))]
    : [];
  if (ids.length === 0) {
    return {};
  }

  const payload = await apiRequest("/api/admin/users/:resolve", {
    method: "POST",
    headers: { ...createAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify({ userIds: ids }),
  });

  return payload?.users && typeof payload.users === "object" ? payload.users : {};
}

export async function fetchAdminUser({ token, userId } = {}) {
  const id = String(userId || "").trim();
  if (!id) {
    throw new ApiError("userId is required", 400);
  }
  return apiRequest(`/api/admin/users/${encodeURIComponent(id)}`, {
    method: "GET",
    headers: createAuthHeaders(token),
    cache: "no-store",
  });
}

/**
 * GET /api/admin/users/{userID}/notes — patient notes history (Plato letters /
 * casenotes), newest first; requires active employee JWT.
 * @returns {Promise<{ patientId?: string, items: Array<Record<string, *>>, count: number }>}
 */
export async function fetchAdminUserNotes({ token, userId, limit = 10, skip = 0 } = {}) {
  const id = String(userId || "").trim();
  if (!id) {
    throw new ApiError("userId is required", 400);
  }
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  if (skip > 0) params.set("skip", String(skip));
  const payload = await apiRequest(
    `/api/admin/users/${encodeURIComponent(id)}/notes?${params.toString()}`,
    {
      method: "GET",
      headers: createAuthHeaders(token),
      cache: "no-store",
    },
  );
  return {
    patientId: String(payload?.patientId || id),
    items: Array.isArray(payload?.items) ? payload.items : [],
    count: Number(payload?.count) || 0,
  };
}

/**
 * POST /api/admin/users/{userID}/phone-duplicates:refresh — recalculate duplicate-phone metadata.
 * @returns {Promise<Record<string, *>>}
 */
export async function refreshAdminUserPhoneDuplicates({ token, userId } = {}) {
  const id = String(userId || "").trim();
  if (!id) {
    throw new ApiError("userId is required", 400);
  }
  return apiRequest(`/api/admin/users/${encodeURIComponent(id)}/phone-duplicates:refresh`, {
    method: "POST",
    headers: createAuthHeaders(token),
  });
}

/**
 * POST /api/admin/users/merge — merge source patient into survivor patient.
 * @returns {{ fromUserId: string, toUserId: string, user?: Record<string, *>, raw: * }}
 */
export async function postAdminUserMerge({ token, fromUserId, toUserId } = {}) {
  const fromId = String(fromUserId || "").trim();
  const toId = String(toUserId || "").trim();
  if (!fromId || !toId) {
    throw new ApiError("fromUserId and toUserId are required", 400);
  }
  if (fromId === toId) {
    throw new ApiError("fromUserId and toUserId must be different", 400);
  }

  const payload = await apiRequest(`/api/admin/users/merge`, {
    method: "POST",
    headers: {
      ...createAuthHeaders(token),
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ fromUserId: fromId, toUserId: toId }),
  });

  return {
    fromUserId: String(payload?.fromUserId || fromId).trim(),
    toUserId: String(payload?.toUserId || toId).trim(),
    user: payload?.user ?? null,
    raw: payload,
  };
}

/**
 * GET /api/admin/users/patients — paginated patient directory (admin). Optional `search` matches
 * display name or phone. Returns the same rich shape as the other admin user endpoints (includes
 * metadata + patientMeta + profile + contacts), so the Synced badge and tags are available.
 * @returns {{ items: Array<Record<string, *>>, total: number, skip: number, limit: number, raw: * }}
 */
export async function fetchAdminPatients({ token, search, skip = 0, limit = 50 } = {}) {
  const query = new URLSearchParams();
  const trimmed = String(search || "").trim();
  if (trimmed) query.set("search", trimmed);
  query.set("skip", String(Math.max(0, Number(skip) || 0)));
  query.set("limit", String(Math.max(1, Math.min(100, Number(limit) || 50))));
  const payload = await apiRequest(`/api/admin/users/patients?${query.toString()}`, {
    method: "GET",
    headers: createAuthHeaders(token),
    cache: "no-store",
  });
  return {
    items: Array.isArray(payload?.items) ? payload.items : [],
    total: Number(payload?.total) || 0,
    skip: Number(payload?.skip) || 0,
    limit: Number(payload?.limit) || 50,
    raw: payload,
  };
}

/**
 * POST /api/admin/users/patients — create a credential-less patient (admin). Patients never log
 * in; the server assigns a synthetic loginId + random password and stamps the caller's tenant.
 * Local fields go under patientMeta.tags / patientMeta.localNotes.
 * @returns {Promise<Record<string, *>>} the created admin user document
 */
export async function createAdminPatient({ token, displayName, firstName, lastName, sex, dob, phone, tags, localNotes } = {}) {
  const body = {
    displayName: String(displayName || "").trim(),
    firstName: String(firstName || "").trim(),
    lastName: String(lastName || "").trim(),
    sex: String(sex || "").trim(),
    dob: String(dob || "").trim(),
    phone: String(phone || "").trim(),
    tags: Array.isArray(tags) ? tags.map((t) => String(t || "").trim()).filter(Boolean) : [],
    localNotes: String(localNotes || "").trim(),
  };
  if (!body.displayName && !body.firstName && !body.lastName) {
    throw new ApiError("a patient name is required", 400);
  }
  return apiRequest(`/api/admin/users/patients`, {
    method: "POST",
    headers: {
      ...createAuthHeaders(token),
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
}

/**
 * POST /api/admin/threads/{threadID}/switch-patient — re-point a thread from its current patient
 * to a different one WITHOUT deleting the original (distinct from merge). Swaps the patient member
 * and re-points the WhatsApp binding. `binding` is "repointed" when a WhatsApp id/phone was
 * derivable for the target, else "membership-only".
 * @returns {{ threadId, previousPatientId, newPatientId, binding, thread, raw }}
 */
export async function switchThreadPatient({ token, threadId, targetPatientUserId } = {}) {
  const tid = String(threadId || "").trim();
  const target = String(targetPatientUserId || "").trim();
  if (!tid) {
    throw new ApiError("threadId is required", 400);
  }
  if (!target) {
    throw new ApiError("targetPatientUserId is required", 400);
  }
  const payload = await apiRequest(`/api/admin/threads/${encodeURIComponent(tid)}/switch-patient`, {
    method: "POST",
    headers: {
      ...createAuthHeaders(token),
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ targetPatientUserId: target }),
  });
  return {
    threadId: String(payload?.threadId || tid).trim(),
    previousPatientId: String(payload?.previousPatientId || "").trim(),
    newPatientId: String(payload?.newPatientId || target).trim(),
    binding: String(payload?.binding || "").trim(),
    thread: payload?.thread ?? null,
    raw: payload,
  };
}

/**
 * PUT /api/admin/users/{userID} — partial admin user update.
 *
 * Send `metadata`, `patientMeta`, `profile`, `contacts`, `phones`, `loginId` and/or `password`; at
 * least one is required. Per the backend contract, `metadata` REPLACES the stored object wholesale,
 * `patientMeta` is DEEP-MERGED (maps merge, arrays replace — so `{ patientMeta: { tags: [...] } }`
 * swaps just the patient tags), and `profile` is field-merged (only non-empty fields overwrite),
 * so a `{ displayName }` patch renames the contact without clobbering other profile fields.
 * `contacts` is merged append-by-E164 (existing phones are kept; a `{ Phones: [{ E164 }] }` patch
 * appends a new number rather than replacing the list), while `phones` REPLACES the phone list
 * wholesale — it is the only channel that can edit or remove a number (`[]` clears them all, and
 * emails are left untouched). Send one or the other; `phones` wins when both are present.
 * `loginId` changes the sign-in id (server lowercases it; 409 on a same-tenant collision);
 * `password` rotates the credential (write-only — it is never returned by any read endpoint).
 * @returns {Promise<Record<string, *>>}
 */
export async function putAdminUser({ token, userId, metadata, patientMeta, profile, contacts, phones, loginId, password } = {}) {
  const id = String(userId || "").trim();
  if (!id) {
    throw new ApiError("userId is required", 400);
  }
  const body = {};
  if (metadata !== undefined) {
    if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) {
      throw new ApiError("metadata must be an object", 400);
    }
    body.metadata = metadata;
  }
  if (patientMeta !== undefined) {
    if (!patientMeta || typeof patientMeta !== "object" || Array.isArray(patientMeta)) {
      throw new ApiError("patientMeta must be an object", 400);
    }
    body.patientMeta = patientMeta;
  }
  if (profile !== undefined) {
    if (!profile || typeof profile !== "object" || Array.isArray(profile)) {
      throw new ApiError("profile must be an object", 400);
    }
    body.profile = profile;
  }
  if (contacts !== undefined) {
    if (!contacts || typeof contacts !== "object" || Array.isArray(contacts)) {
      throw new ApiError("contacts must be an object", 400);
    }
    body.contacts = contacts;
  }
  if (phones !== undefined) {
    if (!Array.isArray(phones)) {
      throw new ApiError("phones must be an array", 400);
    }
    // The server owns validation (digits, dedupe, caps) — send the rows as typed so its errors
    // describe the actual input rather than something reshaped on the way out.
    body.phones = phones.map((p) => ({
      number: String(p?.number ?? p?.e164 ?? p?.E164 ?? "").trim(),
      label: String(p?.label ?? p?.Label ?? "").trim(),
    }));
  }
  if (loginId !== undefined) {
    const trimmed = String(loginId || "").trim();
    if (!trimmed) {
      throw new ApiError("loginId cannot be empty", 400);
    }
    body.loginId = trimmed;
  }
  if (password !== undefined) {
    const pw = String(password || "");
    if (!pw) {
      throw new ApiError("password cannot be empty", 400);
    }
    body.password = pw;
  }
  if (!("metadata" in body) && !("patientMeta" in body) && !("profile" in body) && !("contacts" in body) && !("phones" in body) && !("loginId" in body) && !("password" in body)) {
    throw new ApiError("provide metadata, patientMeta, profile, contacts, phones, loginId or password to update", 400);
  }
  return apiRequest(`/api/admin/users/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: {
      ...createAuthHeaders(token),
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
}

/**
 * @typedef {object} DraftReplyPersona
 * @property {string} id
 * @property {string} label
 * @property {string} description
 * @property {boolean} isDefault
 * @property {Record<string, boolean>} switchDefaults Per-switch defaults applied while the user has no explicit override.
 */

/**
 * @typedef {object} DraftReplySwitchDef
 * @property {string} id
 * @property {string} label
 * @property {string} description
 */

/**
 * Hardcoded fallback for the "Rewrite with AI" persona/switch metadata — mirrors the backend's
 * built-in set so the dialog stays fully functional when
 * GET /api/admin/threads/draft-reply/options fails or returns empty lists.
 */
export const DRAFT_REPLY_AI_FALLBACK_META = Object.freeze({
  personas: Object.freeze([
    Object.freeze({
      id: "warm_professional",
      label: "Warm professional",
      description: "Courteous clinic front-desk voice (default)",
      isDefault: true,
      switchDefaults: Object.freeze({ greetingWithName: true, acknowledge: true, closingOffer: true, offerDoctor: false, signOff: true, expandFromContext: true }),
    }),
    Object.freeze({
      id: "formal_concise",
      label: "Formal & concise",
      description: "Strict minimal rephrasing of the draft, no extras",
      isDefault: false,
      switchDefaults: Object.freeze({ greetingWithName: false, acknowledge: false, closingOffer: true, offerDoctor: false, signOff: true, expandFromContext: true }),
    }),
    Object.freeze({
      id: "empathetic_supportive",
      label: "Empathetic & supportive",
      description: "Gentle voice for sensitive or worrying topics",
      isDefault: false,
      switchDefaults: Object.freeze({ greetingWithName: true, acknowledge: true, closingOffer: true, offerDoctor: true, signOff: true, expandFromContext: true }),
    }),
    Object.freeze({
      id: "friendly_casual",
      label: "Friendly & casual",
      description: "Light and approachable, still polite",
      isDefault: false,
      switchDefaults: Object.freeze({ greetingWithName: false, acknowledge: false, closingOffer: true, offerDoctor: false, signOff: true, expandFromContext: true }),
    }),
  ]),
  switches: Object.freeze([
    Object.freeze({ id: "greetingWithName", label: "Greet by name", description: "Start with \"Dear <patient name>,\" — the name is resolved by the backend" }),
    Object.freeze({ id: "acknowledge", label: "Acknowledge the question", description: "Add a short \"thank you for your question\" clause near the start" }),
    Object.freeze({ id: "closingOffer", label: "Offer further help", description: "End with \"Is there anything else I can help you with?\"" }),
    Object.freeze({ id: "offerDoctor", label: "Offer doctor check", description: "Offer \"Would you like me to check with the doctor?\"" }),
    Object.freeze({ id: "signOff", label: "Sign off with your name", description: "Close the message with your first name (format set in config)" }),
    Object.freeze({
      id: "expandFromContext",
      label: "Expand from context",
      description:
        "Treat your note as a hint and write a full reply using what this conversation already says, instead of just polishing your wording. It never adds medical details the conversation does not contain.",
    }),
  ]),
});

/**
 * GET /api/admin/threads/draft-reply/options — persona + switch metadata for the "Rewrite with
 * AI" dialog; requires active employee JWT. Returns possibly-empty arrays — the caller decides
 * when to fall back to DRAFT_REPLY_AI_FALLBACK_META.
 * @returns {Promise<{ defaultPersona: string, personas: DraftReplyPersona[], switches: DraftReplySwitchDef[], raw: * }>}
 */
export async function getAdminDraftReplyPersonas({ token } = {}) {
  const payload = await apiRequest("/api/admin/threads/draft-reply/options", {
    method: "GET",
    headers: createAuthHeaders(token),
  });

  const rawPersonas = Array.isArray(payload?.personas) ? payload.personas : [];
  /** @type {DraftReplyPersona[]} */
  const personas = rawPersonas
    .map((p) => {
      const id = String(p?.id || "").trim();
      if (!id) return null;
      /** @type {Record<string, boolean>} */
      const switchDefaults = {};
      if (p?.switchDefaults && typeof p.switchDefaults === "object" && !Array.isArray(p.switchDefaults)) {
        for (const [key, value] of Object.entries(p.switchDefaults)) {
          const k = String(key || "").trim();
          if (k && typeof value === "boolean") switchDefaults[k] = value;
        }
      }
      return {
        id,
        label: String(p?.label || "").trim(),
        description: String(p?.description || "").trim(),
        isDefault: Boolean(p?.isDefault),
        switchDefaults,
      };
    })
    .filter(Boolean);

  const rawSwitches = Array.isArray(payload?.switches) ? payload.switches : [];
  /** @type {DraftReplySwitchDef[]} */
  const switches = rawSwitches
    .map((s) => {
      const id = String(s?.id || "").trim();
      if (!id) return null;
      return {
        id,
        label: String(s?.label || "").trim(),
        description: String(s?.description || "").trim(),
      };
    })
    .filter(Boolean);

  return {
    defaultPersona: String(payload?.defaultPersona || "").trim(),
    personas,
    switches,
    raw: payload,
  };
}

/**
 * POST /api/admin/threads/{threadID}/draft-reply — polished reply from raw intent; requires active employee JWT.
 * `persona` (persona id) and `options` (switch id → boolean, resolved by the caller) tune the
 * rewrite; either is omitted from the request when empty so the backend defaults apply.
 * @returns {{ threadId: string, draft: string, raw: * }}
 */
export async function postAdminDraftReply({ token, threadId, text, persona, options } = {}) {
  const normalizedThreadId = String(threadId || "").trim();
  if (!normalizedThreadId) {
    throw new ApiError("threadId is required", 400);
  }
  const trimmed = String(text || "").trim();
  if (!trimmed) {
    throw new ApiError("text is required", 400);
  }

  const body = { text: trimmed };
  const normalizedPersona = String(persona || "").trim();
  if (normalizedPersona) {
    body.persona = normalizedPersona;
  }
  if (options && typeof options === "object" && !Array.isArray(options)) {
    /** @type {Record<string, boolean>} */
    const normalizedOptions = {};
    for (const [key, value] of Object.entries(options)) {
      const k = String(key || "").trim();
      if (k && typeof value === "boolean") normalizedOptions[k] = value;
    }
    if (Object.keys(normalizedOptions).length > 0) {
      body.options = normalizedOptions;
    }
  }

  const payload = await apiRequest(`/api/admin/threads/${encodeURIComponent(normalizedThreadId)}/draft-reply`, {
    method: "POST",
    headers: {
      ...createAuthHeaders(token),
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  return {
    threadId: String(payload?.threadId || normalizedThreadId).trim(),
    draft: String(payload?.draft ?? "").trim(),
    raw: payload,
  };
}

/**
 * POST /api/admin/threads/{threadID}/messages:clear — clear thread history and triage Redis state.
 * When `resetSessionOnly` is true, only Redis session/triage keys are cleared; MongoDB messages are kept.
 * @returns {{ threadId: string, messagesCleared: number, triageRedisKeysCleared: number, resetSessionOnly: boolean, raw: * }}
 */
export async function postAdminClearThreadMessages({ token, threadId, resetSessionOnly = false } = {}) {
  const normalizedThreadId = String(threadId || "").trim();
  if (!normalizedThreadId) {
    throw new ApiError("threadId is required", 400);
  }

  const qs = resetSessionOnly ? "?resetSessionOnly=true" : "";
  const payload = await apiRequest(
    `/api/admin/threads/${encodeURIComponent(normalizedThreadId)}/messages:clear${qs}`,
    {
      method: "POST",
      headers: createAuthHeaders(token),
    },
  );

  return {
    threadId: String(payload?.threadId || normalizedThreadId).trim(),
    messagesCleared: Math.max(0, Number(payload?.messagesCleared) || 0),
    triageRedisKeysCleared: Math.max(0, Number(payload?.triageRedisKeysCleared) || 0),
    resetSessionOnly: Boolean(payload?.resetSessionOnly ?? resetSessionOnly),
    raw: payload,
  };
}

/**
 * DELETE /api/admin/threads/{threadID} — delete a thread AND all its messages (dev/admin
 * cleanup). The server soft-deletes every message, then the thread itself, and clears triage
 * Redis state; connected clients get a thread "deleted" broadcast. Requires active employee
 * JWT; unlike DELETE /api/threads/{threadId} there is no thread-membership requirement.
 * @returns {{ threadId: string, messagesCleared: number, isDeleted: boolean, raw: * }}
 */
export async function deleteAdminThread({ token, threadId } = {}) {
  const normalizedThreadId = String(threadId || "").trim();
  if (!normalizedThreadId) {
    throw new ApiError("threadId is required", 400);
  }
  const payload = await apiRequest(`/api/admin/threads/${encodeURIComponent(normalizedThreadId)}`, {
    method: "DELETE",
    headers: createAuthHeaders(token),
  });
  return {
    threadId: String(payload?.threadId || normalizedThreadId).trim(),
    messagesCleared: Math.max(0, Number(payload?.messagesCleared) || 0),
    isDeleted: Boolean(payload?.isDeleted),
    raw: payload,
  };
}

/**
 * GET /api/admin/users/{userID}/appointments — list appointments for a user.
 * @returns {{
 *  from: string,
 *  to: string,
 *  items: Array<{ id: string, startAt: string, endAt: string, status: string, raw: * }>,
 *  count: number,
 *  raw: *
 * }}
 */
export async function fetchAdminUserAppointments({
  token,
  userId,
  from,
  to,
  includePast = true,
  includeCancelled = false,
  limit = 200,
  skip = 0,
} = {}) {
  const id = String(userId || "").trim();
  if (!id) {
    throw new ApiError("userId is required", 400);
  }

  const query = new URLSearchParams();
  const normalizedFrom = String(from || "").trim();
  const normalizedTo = String(to || "").trim();
  if (normalizedFrom) query.set("from", normalizedFrom);
  if (normalizedTo) query.set("to", normalizedTo);
  query.set("includePast", includePast ? "true" : "false");
  // Surface cancelled appointments (default: hidden) so the panel can show the
  // bot's cancellations and the reason the patient gave.
  query.set("includeCancelled", includeCancelled ? "true" : "false");
  query.set("limit", String(Math.max(1, Math.min(5000, Number(limit) || 200))));
  query.set("skip", String(Math.max(0, Number(skip) || 0)));

  const payload = await apiRequest(
    `/api/admin/users/${encodeURIComponent(id)}/appointments?${query.toString()}`,
    {
      method: "GET",
      headers: createAuthHeaders(token),
    },
  );

  const items = Array.isArray(payload?.items)
    ? payload.items.map((item) => ({
      id: String(item?.id || "").trim(),
      startAt: String(item?.startAt || item?.start || "").trim(),
      endAt: String(item?.endAt || item?.end || "").trim(),
      doctorId: String(item?.doctorId || item?._doctor?.id || "").trim(),
      status: String(item?.status || "").trim(),
      raw: item,
    }))
    : [];

  return {
    from: String(payload?.from || normalizedFrom).trim(),
    to: String(payload?.to || normalizedTo).trim(),
    items,
    count: Number(payload?.count) || items.length,
    raw: payload,
  };
}

/**
 * POST /api/admin/users/{userID}/appointments:cancel — cancel an appointment for that patient.
 * @returns {Promise<{ success?: boolean, appointment?: *, reason?: string, raw?: * }>}
 */
export async function cancelAdminUserAppointment({ token, userId, appointmentId, reason } = {}) {
  const uid = String(userId || "").trim();
  const aid = String(appointmentId || "").trim();
  if (!uid) {
    throw new ApiError("userId is required", 400);
  }
  if (!aid) {
    throw new ApiError("appointmentId is required", 400);
  }

  const body = { appointmentId: aid };
  const trimmedReason = String(reason || "").trim();
  if (trimmedReason) {
    body.reason = trimmedReason;
  }

  return apiRequest(`/api/admin/users/${encodeURIComponent(uid)}/appointments:cancel`, {
    method: "POST",
    headers: {
      ...createAuthHeaders(token),
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
}


/**
 * POST /api/admin/users/{userID}/appointments:update — atomic partial update of
 * an appointment (drag-to-move / resize, doctor reassignment, editor saves).
 * Only fields passed as non-undefined are sent; the backend changes only those
 * and keeps the appointment's identity/episode. `appointmentId` may be the
 * prefixed calendar id ("a-<hex>") — the backend strips the prefix.
 * @returns {Promise<{ success?: boolean, appointment?: *, conflicts?: Array }>}
 */
export async function updateAdminUserAppointment({
  token,
  userId,
  appointmentId,
  title,
  description,
  start,
  end,
  timezone,
  doctorId,
  tenantId,
  calendarId,
  status,
  colorHex,
  lock15m,
  reason,
} = {}) {
  const uid = String(userId || "").trim();
  const aid = String(appointmentId || "").trim();
  if (!uid) {
    throw new ApiError("userId is required", 400);
  }
  if (!aid) {
    throw new ApiError("appointmentId is required", 400);
  }

  const body = { appointmentId: aid };
  if (title !== undefined) body.title = String(title);
  if (description !== undefined) body.description = String(description);
  if (start !== undefined) body.start = String(start);
  if (end !== undefined) body.end = String(end);
  if (timezone !== undefined) body.timezone = String(timezone);
  if (doctorId !== undefined) body.doctorId = String(doctorId);
  if (tenantId !== undefined) body.tenantId = String(tenantId);
  if (calendarId !== undefined) body.calendarId = String(calendarId);
  if (status !== undefined) body.status = String(status);
  // "" clears the per-appointment color override (falls back to the calendar color).
  if (colorHex !== undefined) body.colorHex = String(colorHex);
  // Pins metadata.lock15m (block only the first 15 minutes; full duration still shown).
  if (lock15m !== undefined) body.lock15m = Boolean(lock15m);
  // Staff-entered explanation; persisted to the appointment_history audit trail.
  if (String(reason || "").trim()) body.reason = String(reason).trim();

  return apiRequest(`/api/admin/users/${encodeURIComponent(uid)}/appointments:update`, {
    method: "POST",
    headers: {
      ...createAuthHeaders(token),
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
}


/**
 * POST /api/admin/users/{userID}/appointments:confirmationDraft — render the
 * tenant's per-type confirmation message for an appointment (patient/doctor/
 * date/clinic interpolated). Read-only; staff edit the text before sending.
 * `appointmentId` may keep its "a-" prefix. `userId` may be the NIL sentinel.
 * @returns {Promise<{ text: string }>}
 */
export async function fetchAppointmentConfirmationDraft({ token, userId, appointmentId } = {}) {
  const uid = String(userId || "").trim();
  const aid = String(appointmentId || "").trim();
  if (!uid) throw new ApiError("userId is required", 400);
  if (!aid) throw new ApiError("appointmentId is required", 400);
  return apiRequest(`/api/admin/users/${encodeURIComponent(uid)}/appointments:confirmationDraft`, {
    method: "POST",
    headers: { ...createAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify({ appointmentId: aid }),
  });
}

/**
 * POST /api/admin/users/{userID}/appointments:setConfirmation — stamp the local
 * confirmation state on an appointment. `status`: "pending" (confirmation sent),
 * "confirmed" (manual mark) or "" (clear). `via`: "manual" | "whatsapp".
 * @returns {Promise<{ success?: boolean, appointment?: * }>}
 */
export async function setAdminAppointmentConfirmation({ token, userId, appointmentId, status, via } = {}) {
  const uid = String(userId || "").trim();
  const aid = String(appointmentId || "").trim();
  if (!uid) throw new ApiError("userId is required", 400);
  if (!aid) throw new ApiError("appointmentId is required", 400);
  const body = { appointmentId: aid, status: String(status ?? "") };
  if (via !== undefined) body.via = String(via);
  return apiRequest(`/api/admin/users/${encodeURIComponent(uid)}/appointments:setConfirmation`, {
    method: "POST",
    headers: { ...createAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}


/**
 * GET /api/admin/appointments/{appointmentID}/history — append-only amendment
 * audit rows (newest first): actor, action (updated|cancelled|restored),
 * reason and field-level diffs. `appointmentId` may keep its "a-" prefix.
 * @returns {Promise<{ appointmentId: string, items: Array<{
 *  id: string, action: string, reason?: string, actorUserId?: string,
 *  actorName?: string, at: string,
 *  changes?: Array<{ field: string, from?: string, to?: string }>,
 * }>, count: number, raw: * }>}
 */
export async function fetchAppointmentHistory({ token, appointmentId, limit = 100 } = {}) {
  const aid = String(appointmentId || "").trim();
  if (!aid) {
    throw new ApiError("appointmentId is required", 400);
  }
  const query = new URLSearchParams({ limit: String(Math.max(1, Math.min(500, Number(limit) || 100))) });
  const payload = await apiRequest(
    `/api/admin/appointments/${encodeURIComponent(aid)}/history?${query.toString()}`,
    {
      method: "GET",
      headers: createAuthHeaders(token),
    },
  );
  const items = Array.isArray(payload?.items)
    ? payload.items.map((item) => ({
      id: String(item?.id || "").trim(),
      action: String(item?.action || "").trim(),
      reason: String(item?.reason || "").trim(),
      actorUserId: String(item?.actorUserId || "").trim(),
      actorName: String(item?.actorName || "").trim(),
      at: String(item?.at || "").trim(),
      changes: Array.isArray(item?.changes)
        ? item.changes.map((change) => ({
          field: String(change?.field || "").trim(),
          from: String(change?.from ?? ""),
          to: String(change?.to ?? ""),
        }))
        : [],
    }))
    : [];
  return {
    appointmentId: String(payload?.appointmentId || aid).trim(),
    items,
    count: Number(payload?.count) || items.length,
    raw: payload,
  };
}


/**
 * POST /api/admin/users/{userID}/appointments — book a slot. `body` is the JSON
 * string (or object) the caller built; an optional `calendarId` is merged into it
 * (required to book slots of non-blocking calendars, harmless otherwise).
 */
export async function bookAppointment({ token, normalizedUserId, body, calendarId } = {}) {
  let payload = body;
  const normalizedCalendarId = String(calendarId || "").trim();
  if (normalizedCalendarId) {
    const parsed =
      typeof body === "string"
        ? JSON.parse(body.trim() || "{}")
        : body && typeof body === "object"
          ? body
          : {};
    payload = JSON.stringify({ ...parsed, calendarId: normalizedCalendarId });
  } else if (body && typeof body === "object") {
    payload = JSON.stringify(body);
  }
  return await apiRequest(`/api/admin/users/${encodeURIComponent(normalizedUserId)}/appointments`, {
    method: "POST",
    headers: {
      ...createAuthHeaders(token),
      "Content-Type": "application/json"
    },
    body: payload,
  });
}

// ===========================================================================
// RAG knowledge-base admin endpoints
// ===========================================================================

function buildRagBody({ section, question, answer, aliases, sourceId, metadata }) {
  const cleanAliases = Array.isArray(aliases)
    ? aliases.map((a) => String(a || "").trim()).filter(Boolean)
    : [];
  const cleanMetadata = metadata && typeof metadata === "object" && !Array.isArray(metadata)
    ? { ...metadata }
    : {};
  const trimmedSourceId = String(sourceId || "").trim();
  if (trimmedSourceId) {
    cleanMetadata.sourceId = trimmedSourceId;
  } else if ("sourceId" in cleanMetadata) {
    delete cleanMetadata.sourceId;
  }
  return {
    section: String(section || "").trim(),
    question: String(question || "").trim(),
    answer: String(answer || "").trim(),
    aliases: cleanAliases,
    metadata: cleanMetadata,
  };
}

/**
 * GET /api/admin/rag-items — list FAQ items (paginated).
 */
export async function fetchAdminRagItems({ token, skip = 0, limit = 500 } = {}) {
  const query = new URLSearchParams();
  query.set("skip", String(Math.max(0, Number(skip) || 0)));
  query.set("limit", String(Math.max(1, Math.min(500, Number(limit) || 500))));
  const payload = await apiRequest(`/api/admin/rag-items?${query.toString()}`, {
    method: "GET",
    headers: createAuthHeaders(token),
    cache: "no-store",
  });
  return {
    items: Array.isArray(payload?.items) ? payload.items : [],
    total: Number(payload?.total) || 0,
  };
}

/**
 * GET /api/admin/rag-items/{id} — fetch one item.
 */
export async function getAdminRagItem({ token, id } = {}) {
  const itemId = String(id || "").trim();
  if (!itemId) {
    throw new ApiError("id is required", 400);
  }
  return apiRequest(`/api/admin/rag-items/${encodeURIComponent(itemId)}`, {
    method: "GET",
    headers: createAuthHeaders(token),
    cache: "no-store",
  });
}

/**
 * POST /api/admin/rag-items — create.
 */
export async function createAdminRagItem({ token, section, question, answer, aliases, sourceId, metadata } = {}) {
  const body = buildRagBody({ section, question, answer, aliases, sourceId, metadata });
  return apiRequest(`/api/admin/rag-items`, {
    method: "POST",
    headers: {
      ...createAuthHeaders(token),
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
}

/**
 * PUT /api/admin/rag-items/{id} — replace.
 */
export async function updateAdminRagItem({ token, id, section, question, answer, aliases, sourceId, metadata } = {}) {
  const itemId = String(id || "").trim();
  if (!itemId) {
    throw new ApiError("id is required", 400);
  }
  const body = buildRagBody({ section, question, answer, aliases, sourceId, metadata });
  return apiRequest(`/api/admin/rag-items/${encodeURIComponent(itemId)}`, {
    method: "PUT",
    headers: {
      ...createAuthHeaders(token),
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
}

/**
 * DELETE /api/admin/rag-items/{id} — delete.
 */
export async function deleteAdminRagItem({ token, id } = {}) {
  const itemId = String(id || "").trim();
  if (!itemId) {
    throw new ApiError("id is required", 400);
  }
  // The server returns 204 with empty body; apiRequest handles it (payload will be null).
  return apiRequest(`/api/admin/rag-items/${encodeURIComponent(itemId)}`, {
    method: "DELETE",
    headers: createAuthHeaders(token),
  });
}

// An import body carries exactly one file: markdown text, or an .xlsx workbook as base64.
function buildRagImportBody({ markdown, xlsx, mode }) {
  if (!markdown && !xlsx) {
    throw new ApiError("markdown or xlsx is required", 400);
  }
  const file = xlsx ? { xlsx: String(xlsx) } : { markdown: String(markdown) };
  return { ...file, mode: String(mode || "merge") };
}

/**
 * POST /api/admin/rag-items/import-preview — preview the diff for a markdown file or an
 * .xlsx workbook (`xlsx` = base64 of the file).
 */
export async function previewAdminRagImport({ token, markdown, xlsx, mode = "merge" } = {}) {
  const body = buildRagImportBody({ markdown, xlsx, mode });
  return apiRequest(`/api/admin/rag-items/import-preview`, {
    method: "POST",
    headers: {
      ...createAuthHeaders(token),
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
}

/**
 * POST /api/admin/rag-items/import — apply the import (server-side merge / replace).
 */
export async function applyAdminRagImport({ token, markdown, xlsx, mode = "merge" } = {}) {
  const body = buildRagImportBody({ markdown, xlsx, mode });
  return apiRequest(`/api/admin/rag-items/import`, {
    method: "POST",
    headers: {
      ...createAuthHeaders(token),
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ ...body, confirm: true }),
  });
}

/**
 * POST /api/admin/rag-items/search — embed query + Qdrant similarity search.
 */
export async function searchAdminRagItems({ token, query, limit = 10 } = {}) {
  const trimmed = String(query || "").trim();
  if (!trimmed) {
    throw new ApiError("query is required", 400);
  }
  return apiRequest(`/api/admin/rag-items/search`, {
    method: "POST",
    headers: {
      ...createAuthHeaders(token),
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ query: trimmed, limit: Math.max(1, Math.min(50, Number(limit) || 10)) }),
  });
}

/**
 * Fetches an authenticated export and triggers a browser download via a temporary anchor, named
 * by the server's Content-Disposition (fallbackFilename otherwise).
 */
async function downloadAdminFile({ token, path, fallbackFilename }) {
  const headers = createAuthHeaders(token);
  const response = await fetch(`${getApiBaseUrl()}${path}`, {
    method: "GET",
    headers,
  });
  if (!response.ok) {
    let payload = null;
    try {
      payload = await response.json();
    } catch (_) { /* ignore — not JSON */ }
    throw new ApiError(payload?.error || `export failed (${response.status})`, response.status, payload);
  }
  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") || "";
  const match = /filename="?([^";]+)"?/.exec(disposition);
  const filename = match?.[1] || fallbackFilename;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return { filename, size: blob.size };
}

/**
 * GET /api/admin/rag-items/export.md | export.xlsx — download the current collection as a .md
 * file, or (format "xlsx") as an Excel workbook with one sheet per section.
 */
export async function downloadAdminRagExport({ token, format = "md" } = {}) {
  const ext = format === "xlsx" ? "xlsx" : "md";
  const date = new Date().toISOString().slice(0, 10);
  return downloadAdminFile({
    token,
    path: `/api/admin/rag-items/export.${ext}`,
    fallbackFilename: ext === "xlsx" ? `knowledge-base-${date}.xlsx` : `rag-items-${date}.md`,
  });
}

// ===========================================================================
// Trigger base admin endpoints — twins of the RAG functions above, pointed at
// /api/admin/qa-items. Same request/response shapes; the QA collection mirrors
// the RAG (FAQ) schema exactly (section / question / answer / aliases /
// metadata.sourceId).
// ===========================================================================

/**
 * GET /api/admin/qa-items — list QA items (paginated).
 */
export async function fetchAdminQaItems({ token, skip = 0, limit = 500 } = {}) {
  const query = new URLSearchParams();
  query.set("skip", String(Math.max(0, Number(skip) || 0)));
  query.set("limit", String(Math.max(1, Math.min(500, Number(limit) || 500))));
  const payload = await apiRequest(`/api/admin/qa-items?${query.toString()}`, {
    method: "GET",
    headers: createAuthHeaders(token),
    cache: "no-store",
  });
  return {
    items: Array.isArray(payload?.items) ? payload.items : [],
    total: Number(payload?.total) || 0,
  };
}

/**
 * GET /api/admin/qa-items/{id} — fetch one item.
 */
export async function getAdminQaItem({ token, id } = {}) {
  const itemId = String(id || "").trim();
  if (!itemId) {
    throw new ApiError("id is required", 400);
  }
  return apiRequest(`/api/admin/qa-items/${encodeURIComponent(itemId)}`, {
    method: "GET",
    headers: createAuthHeaders(token),
    cache: "no-store",
  });
}

/**
 * POST /api/admin/qa-items — create.
 */
export async function createAdminQaItem({ token, section, question, answer, aliases, sourceId, metadata, lockAfterAnswer } = {}) {
  // lockAfterAnswer is QA-only — appended outside buildRagBody (shared with the RAG/KB endpoints).
  const body = { ...buildRagBody({ section, question, answer, aliases, sourceId, metadata }), lockAfterAnswer: Boolean(lockAfterAnswer) };
  return apiRequest(`/api/admin/qa-items`, {
    method: "POST",
    headers: {
      ...createAuthHeaders(token),
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
}

/**
 * PUT /api/admin/qa-items/{id} — replace.
 */
export async function updateAdminQaItem({ token, id, section, question, answer, aliases, sourceId, metadata, lockAfterAnswer } = {}) {
  const itemId = String(id || "").trim();
  if (!itemId) {
    throw new ApiError("id is required", 400);
  }
  // lockAfterAnswer is QA-only — appended outside buildRagBody (shared with the RAG/KB endpoints).
  const body = { ...buildRagBody({ section, question, answer, aliases, sourceId, metadata }), lockAfterAnswer: Boolean(lockAfterAnswer) };
  return apiRequest(`/api/admin/qa-items/${encodeURIComponent(itemId)}`, {
    method: "PUT",
    headers: {
      ...createAuthHeaders(token),
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
}

/**
 * DELETE /api/admin/qa-items/{id} — delete.
 */
export async function deleteAdminQaItem({ token, id } = {}) {
  const itemId = String(id || "").trim();
  if (!itemId) {
    throw new ApiError("id is required", 400);
  }
  // The server returns 204 with empty body; apiRequest handles it (payload will be null).
  return apiRequest(`/api/admin/qa-items/${encodeURIComponent(itemId)}`, {
    method: "DELETE",
    headers: createAuthHeaders(token),
  });
}

/**
 * POST /api/admin/qa-items/import-preview — preview the diff for a markdown file or an .xlsx
 * workbook (`xlsx` = base64 of the file).
 */
export async function previewAdminQaImport({ token, markdown, xlsx, mode = "merge" } = {}) {
  const body = buildRagImportBody({ markdown, xlsx, mode });
  return apiRequest(`/api/admin/qa-items/import-preview`, {
    method: "POST",
    headers: {
      ...createAuthHeaders(token),
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
}

/**
 * POST /api/admin/qa-items/import — apply the import (server-side merge / replace).
 */
export async function applyAdminQaImport({ token, markdown, xlsx, mode = "merge" } = {}) {
  const body = buildRagImportBody({ markdown, xlsx, mode });
  return apiRequest(`/api/admin/qa-items/import`, {
    method: "POST",
    headers: {
      ...createAuthHeaders(token),
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ ...body, confirm: true }),
  });
}

/**
 * POST /api/admin/qa-items/search — embed query + Qdrant similarity search.
 */
export async function searchAdminQaItems({ token, query, limit = 10 } = {}) {
  const trimmed = String(query || "").trim();
  if (!trimmed) {
    throw new ApiError("query is required", 400);
  }
  return apiRequest(`/api/admin/qa-items/search`, {
    method: "POST",
    headers: {
      ...createAuthHeaders(token),
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ query: trimmed, limit: Math.max(1, Math.min(50, Number(limit) || 10)) }),
  });
}

/**
 * GET /api/admin/qa-items/export.md | export.xlsx — download the phrases as a .md file, or (format
 * "xlsx") as an Excel workbook with one sheet per category and a Locks after reply column.
 */
export async function downloadAdminQaExport({ token, format = "md" } = {}) {
  const ext = format === "xlsx" ? "xlsx" : "md";
  const date = new Date().toISOString().slice(0, 10);
  return downloadAdminFile({
    token,
    path: `/api/admin/qa-items/export.${ext}`,
    fallbackFilename: ext === "xlsx" ? `trigger-base-${date}.xlsx` : `qa-items-${date}.md`,
  });
}